package com.trevorwiebe.timesheet.admin.employeeHours.domain.usecases

import com.trevorwiebe.timesheet.admin.employeeHours.domain.EmployeeHoursSummary
import com.trevorwiebe.timesheet.admin.employeeHours.domain.PeriodSummary
import com.trevorwiebe.timesheet.core.domain.CoreRepository
import com.trevorwiebe.timesheet.core.domain.TSResult
import com.trevorwiebe.timesheet.core.domain.Util
import com.trevorwiebe.timesheet.core.domain.model.DatabaseUserModel
import com.trevorwiebe.timesheet.core.domain.model.Organization
import com.trevorwiebe.timesheet.core.domain.model.Punch
import com.trevorwiebe.timesheet.core.domain.model.Rate
import com.trevorwiebe.timesheet.core.domain.model.TimeSheet
import com.trevorwiebe.timesheet.core.domain.usecases.GetCurrentPayPeriodStartAndEnd
import com.trevorwiebe.timesheet.payPeriod.domain.PayPeriodRepository
import com.trevorwiebe.timesheet.payPeriod.presentation.uiUtils.TimeSheetStatus
import com.trevorwiebe.timesheet.punch.domain.PunchRepository
import com.trevorwiebe.timesheet.punch.domain.usecases.ProcessPunchesForUi
import kotlinx.coroutines.async
import kotlinx.coroutines.awaitAll
import kotlinx.coroutines.coroutineScope
import kotlinx.datetime.DateTimeUnit
import kotlinx.datetime.LocalDateTime
import kotlinx.datetime.atTime
import kotlinx.datetime.daysUntil
import kotlinx.datetime.minus
import kotlinx.datetime.plus

/**
 * Builds the admin Employee Hour Reports list: for every active employee, the hours-by-rate and
 * submission status for the current pay period and the previous one.
 *
 * Reuses the same building blocks as the employee-facing punch screen — [GetCurrentPayPeriodStartAndEnd]
 * and [ProcessPunchesForUi] — with [AddUpHoursWithOvertime] layered on top so hours past 40 per
 * week are split into per-rate overtime buckets for payroll, plus
 * [Util.getTimeSheetStatus] for the status chips. Punches and timesheets are read per employee via
 * the `userId`-parameterized repositories ([PunchRepository.getPunches] / [PayPeriodRepository.getTimeSheets]),
 * taking the first realtime emission as a one-shot read.
 *
 * Returns a [TSResult] whose `data` is `List<EmployeeHoursSummary>` (sorted by display name), or an
 * error if the organization, rates or user list can't be loaded.
 */
class GetEmployeeHoursReport(
    private val coreRepository: CoreRepository,
    private val punchRepository: PunchRepository,
    private val payPeriodRepository: PayPeriodRepository,
    private val getCurrentPayPeriodStartAndEnd: GetCurrentPayPeriodStartAndEnd,
    private val processPunchesForUi: ProcessPunchesForUi,
    private val addUpHoursWithOvertime: AddUpHoursWithOvertime,
) {
    @Suppress("UNCHECKED_CAST")
    suspend operator fun invoke(): TSResult {
        val orgResult = coreRepository.getOrganization()
        if (orgResult.error != null) return orgResult
        val organization = orgResult.data as Organization

        val ratesResult = punchRepository.getRates()
        if (ratesResult.error != null) return ratesResult
        val rates = ratesResult.data as List<Rate>

        val usersResult = coreRepository.getUserList()
        if (usersResult.error != null) return usersResult
        val users = (usersResult.data as List<DatabaseUserModel>)
            .filter { !it.deleted }
            .sortedBy { it.displayName.lowercase() }

        val (currentStart, currentEnd) = getCurrentPayPeriodStartAndEnd(organization)
        val previousStart = currentStart.date.minus(14, DateTimeUnit.DAY).atTime(0, 0, 0)
        val previousEnd = currentStart.date.minus(1, DateTimeUnit.DAY).atTime(23, 59, 59)

        val ptoRateId = Util.getRateIDFromName("PTO", rates)
        val holidayRateId = Util.getRateIDFromName("Holiday", rates)
        val ptoHolidayIds = listOf(ptoRateId, holidayRateId)

        // Read every employee's punches and timesheets concurrently with one-shot reads. The
        // pre-resolved organization.id is passed down so each read skips re-resolving it via auth.
        val summaries = coroutineScope {
            users.map { user ->
                async {
                    val punchesResult = punchRepository.getPunchesOnce(previousStart, currentEnd, user.id, organization.id)
                    if (!punchesResult.error.isNullOrEmpty()) return@async punchesResult
                    val punches = punchesResult.data as List<Punch>

                    val timeSheetsResult = payPeriodRepository.getTimeSheetsOnce(user.id, organization.id)
                    if (!timeSheetsResult.error.isNullOrEmpty()) return@async timeSheetsResult
                    val timeSheets = timeSheetsResult.data as List<TimeSheet>

                    TSResult(
                        data = EmployeeHoursSummary(
                            employee = user,
                            current = computePeriod(currentStart, currentEnd, punches, rates, ptoHolidayIds, timeSheets, currentPeriod = true),
                            previous = computePeriod(previousStart, previousEnd, punches, rates, ptoHolidayIds, timeSheets, currentPeriod = false),
                            previousTimeSheet = timeSheets.firstOrNull { it.payPeriodStart.date == previousStart.date },
                        )
                    )
                }
            }.awaitAll()
        }

        // Surface the first failed read rather than silently reporting zero hours for that employee.
        summaries.firstOrNull { !it.error.isNullOrEmpty() }?.let { return it }

        return TSResult(data = summaries.map { it.data as EmployeeHoursSummary })
    }

    private fun computePeriod(
        start: LocalDateTime,
        end: LocalDateTime,
        allPunches: List<Punch>,
        rates: List<Rate>,
        ptoHolidayIds: List<String>,
        timeSheets: List<TimeSheet>,
        currentPeriod: Boolean,
    ): PeriodSummary {
        val dateList = buildDateList(start, end)
        val periodPunches = allPunches.filter { it.dateTime in start..end }
        val punchMap = processPunchesForUi(dateList, periodPunches, ptoHolidayIds)
        val uiPunchList = punchMap.values.flatMap { it.first + it.second }
        val hours = addUpHoursWithOvertime(uiPunchList, rates, ptoHolidayIds, start)

        val timeSheet = timeSheets.firstOrNull { it.payPeriodStart.date == start.date }
        // The report labels the columns "Current"/"Previous" already, so the redundant
        // CURRENT_PERIOD chip is dropped here.
        val status = Util.getTimeSheetStatus(timeSheet, currentPeriod)
            .filterNot { it == TimeSheetStatus.CURRENT_PERIOD }

        return PeriodSummary(hoursByRate = hours, status = status)
    }

    private fun buildDateList(start: LocalDateTime, end: LocalDateTime): List<LocalDateTime> {
        val days = start.date.daysUntil(end.date)
        return (0..days).map { start.date.plus(it, DateTimeUnit.DAY).atTime(0, 0) }
    }
}
